import React, { useEffect, useRef, useState } from "react"
import "./BusinessUserList.scss"
import { useNavigate } from "react-router-dom"
import InstituteApi from "../../../../Api/InstituteApi"
import {
  Col,
  Row,
  Table,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from "primereact/toast"
import { StandardPagination, StatusBadge, ActionIcon } from "components/Common"

const BusinessUserList = () => {
  document.title = "Business Users"
  const navigate = useNavigate("")
  const accessToken = localStorage.getItem("accessToken")
  const [businessUsers, setBusinessUsers] = useState([])
  const [filteredBusinessUsers, setFilteredBusinessUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [page, setPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit] = useState(10)
  const [filterStatus, setFilterStatus] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Users", link: "/users" },
    { label: "Business Users" },
  ]

  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  const toast = useRef(null)

  const getBusinessUsers = () => {
    setRefreshing(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }

    InstituteApi.getAllBusinessUser(config)
      .then(response => {
        console.log("API Response:", response.data)
        setLoading(false)
        if (response.data && response.data.institutions) {
          console.log("Setting business users:", response.data.institutions)
          setBusinessUsers(response.data.institutions)
          setFilteredBusinessUsers(response.data.institutions)
          setTotalRecords(response.data.totalRecords || 0)
        } else {
          console.log("No institutions data in response")
          setBusinessUsers([])
          setFilteredBusinessUsers([])
          setTotalRecords(0)
        }
      })
      .catch(error => {
        console.error("Error fetching business users:", error)
        toast.current.show({ severity: "error", summary: "Error", detail: "Failed to fetch business users" })
        setBusinessUsers([])
        setFilteredBusinessUsers([])
        setTotalRecords(0)
      })
      .finally(() => {
        setRefreshing(false)
      })
  }

  useEffect(() => {
    getBusinessUsers()
  }, [accessToken, page, limit])

  useEffect(() => {
    console.log("Current businessUsers:", businessUsers)
    console.log("Current filterStatus:", filterStatus)
    
    if (!businessUsers) return

    let filteredData = [...businessUsers]

    // Filter by status
    if (filterStatus !== "all") {
      filteredData = filteredData.filter(
        user => user.status === filterStatus,
      )
    }

    // Filter by search term
    if (searchTerm) {
      filteredData = filteredData.filter(user =>
        user.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.verificationDetails?.businessOwner?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.verificationDetails?.businessOwnerEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    console.log("Filtered data:", filteredData)
    setFilteredBusinessUsers(filteredData)
  }, [filterStatus, businessUsers, searchTerm])

  const totalPages = Math.ceil(totalRecords / limit)

  const handleViewUser = user => {
    setSelectedUser(user)
    setIsModalOpen(true)
  }

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen)
    if (!isModalOpen) setSelectedUser(null)
  }



  return (
    <div className="Business-user-data-table">
      <Toast ref={toast} />
      <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>Business User Details</ModalHeader>
        <ModalBody>
          {selectedUser && (
            <Card>
              <CardBody>
                <div className="business-user-details">
                  <h5 className="mb-4">Basic Information</h5>
                  <Row className="mb-3">
                    <Col md={6}>
                      <p>
                        <strong>Business Name:</strong> {selectedUser.businessName || "-"}
                      </p>
                      <p>
                        <strong>Business Owner:</strong> {selectedUser.verificationDetails?.businessOwner || "-"}
                      </p>
                      <p>
                        <strong>Email:</strong> {selectedUser.verificationDetails?.businessOwnerEmail || "-"}
                      </p>
                      <p>
                        <strong>Phone:</strong> {selectedUser.verificationDetails?.businessOwnerPhone || "-"}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p>
                        <strong>Status:</strong>{" "}
                        <StatusBadge 
                          status={selectedUser.status}
                        />
                      </p>
                    </Col>
                  </Row>
                </div>
              </CardBody>
            </Card>
          )}
        </ModalBody>
      </Modal>

      <Row>
        <Col xs="12">
          <div className="Business-User-List-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb
              data={breadcrumbItems}
              pageTitle="Business Users"
              handleClick={handleBreadcrumbClick}
            />

            <div className="d-flex align-items-center gap-3">
              <div className="search-container">
                <Input
                  type="text"
                  placeholder="Search business users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <i className="mdi mdi-magnify search-icon"></i>
              </div>
              
              <button 
                className="btn btn-outline-primary btn-sm"
                onClick={getBusinessUsers}
                disabled={refreshing}
                title="Refresh Data"
              >
                <i className={`mdi mdi-refresh ${refreshing ? 'mdi-spin' : ''}`}></i>
              </button>
              
              <Input
                type="select"
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                style={{ width: "180px" }}
                className="status-filter"
              >
                <option value="all">All Status</option>
                <option value="VERIFIED">Verified</option>
                <option value="REJECTED">Rejected</option>
                <option value="DOCUMENT_SUBMITTED">Document Submitted</option>
                <option value="ACCOUNT_CREATED">Account Created</option>
              </Input>
            </div>
          </div>

          <div className="data-table-container">
            <div className="table-responsive mt-4 mb-3">
              {loading ? (
                <DataTableLoader cols={7} />
              ) : (
                <Table className="table mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Business Name</th>
                    <th>Business Owner</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBusinessUsers && filteredBusinessUsers.length > 0 ? (
                    filteredBusinessUsers.map((user, index) => (
                      <tr key={user._id} className="Business-user-table-row">
                        <th scope="row">{(page - 1) * limit + index + 1}</th>
                        <td>{user.businessName || "-"}</td>
                        <td>{user.verificationDetails?.businessOwner || "-"}</td>
                        <td>{user.verificationDetails?.businessOwnerEmail || "-"}</td>
                        <td>{user.verificationDetails?.businessOwnerPhone || "-"}</td>
                        <td>
                          <StatusBadge 
                            status={user.status}
                          />
                        </td>
                        <td>
                          <ActionIcon
                            type="view"
                            onClick={() => handleViewUser(user)}
                            tooltip="View Business User"
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No business users found
                      </td>
                    </tr>
                  )}
                </tbody>
                </Table>
              )}
            </div>
          </div>

          <StandardPagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </Col>
      </Row>
    </div>
  )
}

export default BusinessUserList
